import { UIFlightSegment, UISearchResults, UITravellerPrice } from "@flights/types";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { trackWithDefaultStage } from "@flights/et-universal";
import {
  isOfMetaOrigin,
  isOfGoogleFlightsOrigin,
  isOfKayakOrigin,
  isOfSkyScannerOrigin,
  isOfWegoOrigin,
  isOfFlygresorOrigin,
  isOfGoogleSearchWidgetOrigin
} from "../utils/marketing-url-params";
import useEvents from "./useEvents";
import useUserAgent from "./useUserAgent";
import useFormatPrice from "./useFormatPrice";
import { useSelector } from "react-redux";
import { getMetaFlight } from "../store/metaFlightDetails/selectors";

const getMetaSource = () => {
  if (isOfGoogleFlightsOrigin()) return "google_flights";
  if (isOfKayakOrigin()) return "kayak";
  if (isOfSkyScannerOrigin()) return "skyscanner";
  if (isOfWegoOrigin()) return "wego";
  if (isOfFlygresorOrigin()) return "flygresor";
  if (isOfGoogleSearchWidgetOrigin()) return "google_search_widget";
  return "unknown_meta";
};

const getCabinClasses = (segments: UIFlightSegment[]) =>
  [...new Set(segments.flatMap((seg) => seg.legs.map((leg) => leg.cabinClass)))].join(",");

const getPassengerInfo = (travellerPrices: UITravellerPrice[]) => ({
  count: travellerPrices?.length || 0,
  types: travellerPrices?.map((tp) => tp.travellerType).join(",") || "unknown"
});

export const useMetaOfferPriceComparison = () => {
  const { grumble } = useEvents();
  const { isMobile } = useUserAgent();
  const { priceToValue } = useFormatPrice();
  const metaFlight = useSelector(getMetaFlight);

  const experimentName = isMobile
    ? "flights_web_cat_pinning_meta_offer_on_sr_mdot"
    : "flights_web_cat_pinning_meta_offer_on_sr_www";

  const metaSource = getMetaSource();

  const compareAndLogPrices = (searchResults: UISearchResults) => {
    const isPinningMetaOfferEnabled = !!trackWithDefaultStage(experimentName, 1);

    if (!isPinningMetaOfferEnabled || !isOfMetaOrigin()) {
      return;
    }

    const landingOfferKey = flightsSessionStore.get("landingOfferKey");

    if (!landingOfferKey || !metaFlight || searchResults.flightOffers.length === 0) return;

    const searchResultOffer = searchResults.flightOffers.find((offer) => offer.offerKeyToHighlight === landingOfferKey);

    if (searchResultOffer) {
      const originalTotal = metaFlight.priceBreakdown?.total;
      const newTotal = searchResultOffer.priceBreakdown?.total;
      const originalPrice = originalTotal ? priceToValue(originalTotal) : null;
      const newPrice = newTotal ? priceToValue(newTotal) : null;
      const originalCurrency = originalTotal?.currencyCode;
      const newCurrency = newTotal?.currencyCode;
      const originalOfferToken = metaFlight.token;
      const newOfferToken = searchResultOffer.token;
      const searchId = searchResults.searchId;
      const timestamp = Date.now();

      const originalCabinClasses = getCabinClasses(metaFlight.segments);
      const newCabinClasses = getCabinClasses(searchResultOffer.segments);
      const originalPassengers = getPassengerInfo(metaFlight.travellerPrices);
      const newPassengers = getPassengerInfo(searchResultOffer.travellerPrices);

      let priceChangeType: string;
      let priceDifference: number | null = null;
      let priceChangePercentage: number | null = null;

      if (originalCurrency !== newCurrency) {
        priceChangeType = "currency_mismatch";
      } else if (originalPrice === null || newPrice === null) {
        priceChangeType = "unknown";
      } else if (originalPrice === newPrice) {
        priceChangeType = "match";
        priceDifference = 0;
        priceChangePercentage = 0;
      } else {
        priceDifference = newPrice - originalPrice;
        priceChangePercentage = (priceDifference / originalPrice) * 100;
        priceChangeType = priceDifference < 0 ? "cheaper" : "more_expensive";
      }

      // Only log discrepancies - skip perfect matches to reduce noise
      if (priceChangeType === "match") {
        grumble(`Meta offer pinning: price match (${isMobile ? "mdot" : "www"})`, {
          landingOfferKey,
          metaSource,
          searchId
        });
      } else {
        grumble(`Meta offer pinning: price discrepancy (${isMobile ? "mdot" : "www"})`, {
          landingOfferKey,
          metaSource,
          originalOfferToken,
          newOfferToken,
          offerTokenMatch: originalOfferToken === newOfferToken,
          searchId,
          timestamp,
          originalPrice,
          originalCurrency,
          newPrice,
          newCurrency,
          priceChangeType,
          priceDifference,
          priceChangePercentage,
          originalCabinClasses,
          newCabinClasses,
          cabinClassMatch: originalCabinClasses === newCabinClasses,
          originalPassengerCount: originalPassengers.count,
          newPassengerCount: newPassengers.count,
          passengerCountMatch: originalPassengers.count === newPassengers.count,
          originalPassengerTypes: originalPassengers.types,
          newPassengerTypes: newPassengers.types,
          passengerTypesMatch: originalPassengers.types === newPassengers.types
        });
      }
    } else {
      grumble(`Meta offer pinning: offer not found in search results (${isMobile ? "mdot" : "www"})`, {
        landingOfferKey,
        metaSource,
        searchResultsCount: searchResults.flightOffers.length
      });
    }
  };

  return { compareAndLogPrices };
};
